import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
    autoUpdate,
    flip,
    FloatingPortal,
    offset,
    shift,
    size,
    useFloating,
} from "@floating-ui/react";
import { useSvgDiagram2D } from "./SvgDiagram2DContext";

/** The kind of canvas element a right-click landed on. */
export type CanvasTargetKind =
    | "node"
    | "ellipse"
    | "line"
    | "arc"
    | "rect"
    | "polygon";

/**
 * What the canvas right-click resolved to: the element kind and its id. The
 * viewer builds this from the `data-*-id` attribute on the clicked element and
 * selects the element before opening the menu, so the menu's actions (which
 * operate on the current selection) target the right thing.
 */
export interface CanvasContextTarget {
    kind: CanvasTargetKind;
    id: string;
}

export interface CanvasContextMenuState {
    /** Cursor position in viewport (client) pixels, where the menu opens. */
    x: number;
    y: number;
    target: CanvasContextTarget;
}

interface SvgDiagram2DContextMenuProps {
    menu: CanvasContextMenuState;
    onClose: () => void;
}

/** One row in the menu; a separator carries no label. */
type MenuItem =
    | { kind: "separator" }
    | {
          kind: "item";
          label: string;
          onClick: () => void;
          disabled?: boolean;
          /** When set, the row opens a submenu instead of firing on click. */
          submenu?: MenuItem[];
      };

const PASTE_AVAILABLE_NOTE =
    "Paste a previously copied selection near the cursor.";

/**
 * Right-click menu for a canvas element. Offers the standard clipboard actions
 * (cut / copy / paste / duplicate / delete) for any shape, plus a Groups
 * submenu for nodes that adds the node to / removes it from a group. All actions
 * dispatch to the diagram context and operate on the current selection, which
 * the viewer has already set to the right-clicked element.
 */
export function SvgDiagram2DContextMenu({
    menu,
    onClose,
}: SvgDiagram2DContextMenuProps) {
    const {
        doc,
        selectedNodeIds,
        copySelectedNodes,
        cutSelectedNodes,
        pasteNodes,
        copySelectedNodeStyle,
        pasteNodeStyle,
        createGroupFromSelection,
        addNodeToGroup,
        removeNodeFromGroup,
        revealGroup,
        selectNodesInRect,
        duplicateNode,
        duplicateEllipse,
        duplicateLine,
        duplicateArc,
        duplicateRect,
        duplicatePolygon,
        removeSelectedNodes,
        removeSelectedEllipses,
        removeSelectedLines,
        removeSelectedArcs,
        removeSelectedRects,
        removeSelectedPolygons,
    } = useSvgDiagram2D();

    const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);

    // Anchor the menu to the cursor via a virtual reference element, and let
    // Floating UI flip/shift it so it never opens off-screen near an edge.
    const { refs, floatingStyles } = useFloating({
        placement: "right-start",
        whileElementsMounted: autoUpdate,
        middleware: [flip(), shift({ padding: 8 })],
    });

    const virtualReference = useMemo(
        () => ({
            getBoundingClientRect: () =>
                ({
                    x: menu.x,
                    y: menu.y,
                    top: menu.y,
                    left: menu.x,
                    right: menu.x,
                    bottom: menu.y,
                    width: 0,
                    height: 0,
                }) as DOMRect,
        }),
        [menu.x, menu.y],
    );

    useEffect(() => {
        refs.setReference(virtualReference);
    }, [refs, virtualReference]);

    // Dismiss on any outside pointer-down, Escape, scroll, or window blur.
    useEffect(() => {
        const onPointerDown = (e: PointerEvent) => {
            // The root menu and its submenu are portaled separately; treat a
            // press inside either as "inside" so submenu clicks don't dismiss.
            const t = e.target as Element | null;
            if (!t?.closest?.("[data-diag-context-menu]")) onClose();
        };
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                e.stopPropagation();
                onClose();
            }
        };
        // Capture phase so a press anywhere closes us before it does anything
        // else (e.g. starting a drag on the canvas).
        window.addEventListener("pointerdown", onPointerDown, true);
        window.addEventListener("keydown", onKeyDown, true);
        window.addEventListener("blur", onClose);
        return () => {
            window.removeEventListener("pointerdown", onPointerDown, true);
            window.removeEventListener("keydown", onKeyDown, true);
            window.removeEventListener("blur", onClose);
        };
    }, [onClose]);

    const isNode = menu.target.kind === "node";

    /** Wrap an action so the menu closes right after it runs. */
    const run = (fn: () => void) => () => {
        fn();
        onClose();
    };

    // Duplicate + delete dispatch per element kind (selection already points at
    // the right-clicked element).
    const duplicateTarget = () => {
        switch (menu.target.kind) {
            case "node":
                duplicateNode(menu.target.id);
                break;
            case "ellipse":
                duplicateEllipse(menu.target.id);
                break;
            case "line":
                duplicateLine(menu.target.id);
                break;
            case "arc":
                duplicateArc(menu.target.id);
                break;
            case "rect":
                duplicateRect(menu.target.id);
                break;
            case "polygon":
                duplicatePolygon(menu.target.id);
                break;
        }
    };
    const deleteTarget = () => {
        switch (menu.target.kind) {
            case "node":
                removeSelectedNodes();
                break;
            case "ellipse":
                removeSelectedEllipses();
                break;
            case "line":
                removeSelectedLines();
                break;
            case "arc":
                removeSelectedArcs();
                break;
            case "rect":
                removeSelectedRects();
                break;
            case "polygon":
                removeSelectedPolygons();
                break;
        }
    };

    // Build the Groups submenu (nodes only). It lists every group on the node's
    // layer for "Add to group", plus "New group from selection", plus "Remove
    // from group" entries for whichever groups the node already belongs to.
    const buildGroupsSubmenu = (): MenuItem[] => {
        const nodeId = menu.target.id;
        const node = doc.nodes.find((n) => n.id === nodeId);
        if (!node) return [];
        // Groups can't span layers, so only same-layer groups are joinable.
        const sameLayerGroups = doc.groups.filter(
            (g) => g.layerId === node.layerId,
        );
        const memberGroups = doc.groups.filter((g) =>
            g.nodeIds.includes(nodeId),
        );
        const memberIds = new Set(memberGroups.map((g) => g.id));
        const joinable = sameLayerGroups.filter((g) => !memberIds.has(g.id));

        const items: MenuItem[] = [];

        // For a node already in one or more groups, offer to select all the
        // group's member nodes on the canvas, and to jump to each parent group
        // in the panel (expands the Groups list, scrolls to the row, and opens
        // its editor popover).
        if (memberGroups.length > 0) {
            for (const g of memberGroups) {
                items.push({
                    kind: "item",
                    label: `Select all in ${g.name}`,
                    onClick: run(() => selectNodesInRect(g.nodeIds, false)),
                });
            }
            items.push({ kind: "separator" });
            for (const g of memberGroups) {
                items.push({
                    kind: "item",
                    label: `Reveal ${g.name} in panel`,
                    onClick: run(() => revealGroup(g.id)),
                });
            }
            items.push({ kind: "separator" });
        }

        items.push({
            kind: "item",
            label: "New group from selection",
            onClick: run(() =>
                createGroupFromSelection(
                    selectedNodeIds.length > 0 ? selectedNodeIds : [nodeId],
                ),
            ),
        });

        if (joinable.length > 0) {
            items.push({ kind: "separator" });
            for (const g of joinable) {
                items.push({
                    kind: "item",
                    label: `Add to ${g.name}`,
                    onClick: run(() => addNodeToGroup(nodeId, g.id)),
                });
            }
        }

        if (memberGroups.length > 0) {
            items.push({ kind: "separator" });
            for (const g of memberGroups) {
                items.push({
                    kind: "item",
                    label: `Remove from ${g.name}`,
                    onClick: run(() => removeNodeFromGroup(nodeId, g.id)),
                });
            }
        }
        return items;
    };

    const items: MenuItem[] = [
        {
            kind: "item",
            label: "Cut",
            onClick: run(cutSelectedNodes),
        },
        {
            kind: "item",
            label: "Copy",
            onClick: run(copySelectedNodes),
        },
        {
            kind: "item",
            label: "Paste",
            onClick: run(pasteNodes),
        },
        {
            kind: "item",
            label: "Duplicate",
            onClick: run(duplicateTarget),
        },
    ];

    if (isNode) {
        items.push(
            { kind: "separator" },
            {
                kind: "item",
                label: "Copy style",
                onClick: run(copySelectedNodeStyle),
            },
            {
                kind: "item",
                label: "Paste style",
                onClick: run(pasteNodeStyle),
            },
            {
                kind: "item",
                label: "Groups",
                onClick: () => {},
                submenu: buildGroupsSubmenu(),
            },
        );
    }

    items.push(
        { kind: "separator" },
        {
            kind: "item",
            label: "Delete",
            onClick: run(deleteTarget),
        },
    );

    return (
        <FloatingPortal>
            <div
                ref={refs.setFloating}
                className="diag-context-menu"
                style={floatingStyles}
                data-diag-context-menu
                // The menu lives outside the SVG; stop a click here from bubbling
                // to the canvas (which would clear the selection).
                onPointerDown={(e) => e.stopPropagation()}
                onContextMenu={(e) => e.preventDefault()}
                role="menu"
            >
                {items.map((item, i) => {
                    if (item.kind === "separator") {
                        return (
                            <div
                                key={`sep-${i}`}
                                className="diag-context-menu__sep"
                            />
                        );
                    }
                    if (item.submenu) {
                        return (
                            <SubmenuRow
                                key={item.label}
                                label={item.label}
                                items={item.submenu}
                                open={openSubmenu === item.label}
                                onOpen={() => setOpenSubmenu(item.label)}
                            />
                        );
                    }
                    return (
                        <div
                            key={item.label}
                            className="diag-context-menu__row-wrap"
                            onPointerEnter={() => setOpenSubmenu(null)}
                        >
                            <button
                                type="button"
                                role="menuitem"
                                className="diag-context-menu__item"
                                disabled={item.disabled}
                                title={
                                    item.label === "Paste"
                                        ? PASTE_AVAILABLE_NOTE
                                        : undefined
                                }
                                onClick={
                                    item.disabled ? undefined : item.onClick
                                }
                            >
                                <span>{item.label}</span>
                            </button>
                        </div>
                    );
                })}
            </div>
        </FloatingPortal>
    );
}

interface SubmenuRowProps {
    label: string;
    items: MenuItem[];
    open: boolean;
    onOpen: () => void;
}

/**
 * A menu row that opens a submenu on hover. The submenu is anchored to the row
 * via Floating UI and portaled, so it flips to the other side and shifts to
 * stay on-screen near the viewport edges instead of overflowing.
 */
function SubmenuRow({ label, items, open, onOpen }: SubmenuRowProps) {
    const { refs, floatingStyles } = useFloating({
        open,
        placement: "right-start",
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(2),
            flip(),
            shift({ padding: 8 }),
            size({
                padding: 8,
                apply({ availableHeight, elements }) {
                    // A long Groups list near the bottom edge would overflow the
                    // viewport; cap its height and let it scroll instead.
                    elements.floating.style.maxHeight = `${availableHeight}px`;
                },
            }),
        ],
    });

    return (
        <div
            className="diag-context-menu__row-wrap"
            onPointerEnter={onOpen}
        >
            <button
                ref={refs.setReference}
                type="button"
                role="menuitem"
                className="diag-context-menu__item diag-context-menu__item--submenu"
            >
                <span>{label}</span>
                <span className="diag-context-menu__chevron">›</span>
            </button>
            {open && (
                <FloatingPortal>
                    <div
                        ref={refs.setFloating}
                        className="diag-context-menu diag-context-menu__submenu"
                        style={floatingStyles}
                        data-diag-context-menu
                        role="menu"
                    >
                        {renderSubmenuItems(items)}
                    </div>
                </FloatingPortal>
            )}
        </div>
    );
}

function renderSubmenuItems(items: MenuItem[]): ReactNode {
    if (items.length === 0) {
        return <div className="diag-context-menu__empty">No groups</div>;
    }
    return items.map((sub, si) => {
        if (sub.kind === "separator") {
            return (
                <div key={`sub-sep-${si}`} className="diag-context-menu__sep" />
            );
        }
        return (
            <button
                key={sub.label}
                type="button"
                role="menuitem"
                className="diag-context-menu__item"
                onClick={sub.onClick}
            >
                <span>{sub.label}</span>
            </button>
        );
    });
}
